import styled from 'styled-components'
import { Button, colors } from '@stokr/components-library'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace, stoType } from 'constants/stoTheme'

export const Stack = styled.div<{ $gap: keyof typeof stoSpace }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => stoSpace[$gap]};
  min-width: 0;
`

export const Caption = styled.p`
  ${stoType.bodyMd};
  margin: 0;
  color: ${stoColor.black};

  ${stoMedia.belowDesktop} {
    font-weight: 400;
    letter-spacing: 0.6px;
  }
`

/** Two cards per row, one on narrow containers; a lone card keeps its half width. */
export const CardGrid = styled.div<{ $gap: keyof typeof stoSpace }>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ $gap }) => stoSpace[$gap]};

  @container (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

/** Query container for `CardGrid`, so the layout follows the section width, not the viewport. */
export const Root = styled.div`
  container-type: inline-size;
`

export const Card = styled.div<{ $gap: keyof typeof stoSpace }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => stoSpace[$gap]};
  min-width: 0;
  padding: ${stoSpace[5]};
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: ${stoShadow.xs};

  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[4]};
  }
`

export const Logo = styled.img`
  display: block;
  height: 32px;
  max-width: 144px;
  object-fit: contain;
  object-position: left center;
`

export const VenueInfo = styled.div`
  display: flex;
  flex: 1 1 180px;
  flex-direction: column;
  gap: ${stoSpace[2]};
  min-width: 0;
`

export const VenueName = styled.p`
  ${stoType.bodyMdCallout};
  margin: 0;
  color: ${stoColor.textPrimary};
`

export const PairLine = styled.p`
  ${stoType.bodyMd};
  margin: 0;
  color: ${stoColor.textPrimary};

  strong {
    font-weight: 600;
  }
`

export const GroupLabel = styled.p`
  ${stoType.columnLabel};
  margin: 0;
  color: ${stoColor.textSecondary};
`

/**
 * Library `Button` in its black/outlined variant, rendered as a link. `color` is
 * `!important` per state for the same reason as `StepBadgeButton`: the global
 * `a { color: inherit !important }` would otherwise win.
 */
export const ExchangeButton = styled(Button)`
  ${stoType.labelCta};
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${stoSpace[2]};
  min-height: auto;
  padding: 10px ${stoSpace[6]};
  white-space: nowrap;
  border: 2px solid ${stoColor.borderSubtle};
  color: ${stoColor.textPrimary} !important;

  &:hover,
  &:focus,
  &:active {
    border-color: ${stoColor.textPrimary};
    color: ${stoColor.buttonLabelOnFill} !important;
  }
`

export const VenueFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${stoSpace[4]};

  ${ExchangeButton} {
    align-self: flex-end;
  }

  /* Figma 3582:128826: venue buttons run full width on mobile. */
  ${stoMedia.belowDesktop} {
    flex-direction: column;
    align-items: stretch;

    ${VenueInfo} {
      flex: none;
    }

    ${ExchangeButton} {
      align-self: stretch;
      justify-content: center;
    }
  }
`

/** Restyles the library `ReactTable` to the Figma data grid. */
export const TableFrame = styled.div`
  overflow: hidden;
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: 6px;

  th {
    padding: ${stoSpace[2]} ${stoSpace[4]};
    height: 56px;
    background: ${stoColor.surfaceAlt};
    border-bottom: 1px solid ${stoColor.borderSubtle};
    color: ${stoColor.textPrimary};
    font-size: 11px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 2px;
    text-align: left;
    text-transform: uppercase;
  }

  td {
    height: 72px;
    padding: ${stoSpace[4]};
    border-bottom: 1px solid ${stoColor.borderSubtle};
    color: ${stoColor.textPrimary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    /* Numbers never break; the library wrapper scrolls the table sideways when it is too wide. */
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`

export const PairLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${stoSpace[1]};
  font-weight: 600;
  text-decoration: none;
`

export const Approx = styled.span`
  ${stoType.bodySm};
  display: block;
  color: ${stoColor.textSecondary};
`

export const Change = styled.span<{ $direction: number }>`
  color: ${({ $direction }) =>
    $direction > 0 ? colors.freshGreen : $direction < 0 ? colors.orangishRed : stoColor.textPrimary};
`

/**
 * Bid and offer boxes share a row while both fit their values on one line; otherwise the
 * second drops below at full width. Driven by the values' own width, so short USD prices
 * stay side by side and long 8-decimal BTC values stack.
 */
export const QuoteRow = styled.div`
  container-type: inline-size;
  display: flex;
  flex-wrap: wrap;
  gap: ${stoSpace[4]};
`

/**
 * Never narrower than its values, except on phones, where it becomes a stacked list.
 * Flex, not equal columns: a long value takes the room its short neighbour leaves.
 */
export const QuoteBox = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-wrap: wrap;
  min-width: max-content;
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};

  @container (max-width: 320px) {
    flex-basis: 100%;
    min-width: 0;
  }
`

export const Quote = styled.div<{ $align: 'start' | 'end' }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-items: flex-${({ $align }) => $align};
  padding: ${stoSpace[4]};
  text-align: ${({ $align }) => ($align === 'end' ? 'right' : 'left')};

  /* Phones: one left-aligned label/value list, rather than halves that wrap unevenly. */
  @container (max-width: 320px) {
    flex-basis: 100%;
    align-items: flex-start;
    text-align: left;

    & + & {
      padding-top: 0;
    }
  }
`

export const QuoteLabel = styled.span`
  ${stoType.columnLabel};
  color: ${stoColor.textSecondary};
`

export const QuoteValue = styled.span<{ $tone?: 'bid' | 'ask' }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.8px;
  white-space: nowrap;
  color: ${({ $tone }) =>
    $tone === 'bid' ? colors.freshGreen : $tone === 'ask' ? colors.orangishRed : stoColor.textPrimary};
`
